(() => {
  const deferredVideos = new Map();
  const hydratedVideos = new WeakSet();
  const videoMotion = matchMedia('(prefers-reduced-motion: reduce)');
  function hydrateVideo(video) {
    if (!video || hydratedVideos.has(video)) return;
    const sources = [...video.querySelectorAll('source[data-src]')];
    if (!sources.length) return;
    sources.forEach(source => source.src = source.dataset.src);
    hydratedVideos.add(video);
    video.load();
  }
  function playVideo(video) {
    hydrateVideo(video);
    return video.play();
  }
  function syncDeferredVideo(video) {
    const state = deferredVideos.get(video);
    if (!state || video.dataset.videoAutoplay !== 'true') return;
    if (!state.visible || document.hidden || videoMotion.matches || navigator.connection?.saveData) {
      video.pause();
      return;
    }
    if (state.pending || !video.paused) return;
    state.pending = Promise.resolve(playVideo(video)).catch(() => {}).finally(() => {
      state.pending = null;
      if (!state.visible || document.hidden || videoMotion.matches) video.pause();
    });
  }
  const deferredVisibility = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = deferredVideos.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
      if (state.visible && entry.target.controls) hydrateVideo(entry.target);
      if (!state.visible) entry.target.pause();
      syncDeferredVideo(entry.target);
    });
  }, { threshold: [0, 0.1] });
  function initializeDeferredVideos(root) {
    root.querySelectorAll('video[data-deferred-video]').forEach(video => {
      if (deferredVideos.has(video)) return;
      const events = new AbortController();
      deferredVideos.set(video, { visible: false, pending: null, events });
      video.addEventListener('pointerdown', () => hydrateVideo(video), { signal: events.signal, passive: true });
      deferredVisibility.observe(video);
    });
  }
  function destroyDeferredVideos(root) {
    root.querySelectorAll('video[data-deferred-video]').forEach(video => {
      deferredVideos.get(video)?.events.abort();
      video.pause();
      deferredVisibility.unobserve(video);
      deferredVideos.delete(video);
    });
  }
  const syncDeferredVideos = () => deferredVideos.forEach((state, video) => syncDeferredVideo(video));
  videoMotion.addEventListener('change', syncDeferredVideos);
  document.addEventListener('visibilitychange', syncDeferredVideos);
  window.RCLazyVideo = { load: hydrateVideo, play: playVideo };
  document.dispatchEvent(new Event('rc:video-ready'));
  const videoRails = new WeakMap();

  function initializeVideoRail(group) {
    if (videoRails.has(group)) return;
    const rail = group.querySelector('[data-video-track]');
    if (!rail) return;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    const events = new AbortController();
    const enabled = group.dataset.autoScroll === 'true';
    const autoplayEnabled = group.dataset.autoplayVideos !== 'false';
    const speed = Math.max(10, Math.min(80, Number(group.dataset.scrollSpeed) || 35));
    const originals = [...rail.children];
    const copies = [];
    const cloneSources = new WeakMap();
    const videos = new Map();
    const state = {
      paused: false, hovered: false, focused: false, playing: false,
      selected: false, visible: false, interactionUntil: 0, frame: 0,
      time: 0, position: rail.scrollLeft, lastWritten: rail.scrollLeft, destroyed: false
    };
    const listen = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: events.signal });
    const hasOverflow = () => rail.children.length > 1 && rail.scrollWidth > rail.clientWidth + 2;
    const allowPreviews = () => autoplayEnabled && !reducedMotion.matches && !connection?.saveData;
    const canPreview = record => allowPreviews() && record.visible && !record.userPaused && !videos.get(record.sourceVideo)?.userPaused && !state.selected && !document.hidden && !state.destroyed;
    const syncVideoCard = video => {
      const item = video.closest('.rc-video-gallery__item');
      if (!item) return;
      const playing = !video.paused;
      item.setAttribute('aria-pressed', String(playing));
      const label = playing ? item.dataset.pauseLabel : item.dataset.playLabel;
      item.setAttribute('aria-label', [label, video.getAttribute('aria-label')].filter(Boolean).join(': '));
    };
    const pauseForInteraction = () => { state.interactionUntil = performance.now() + 7000; };
    const updatePlaying = () => {
      // Muted ambient previews never interrupt the scrolling rail.
      state.playing = [...videos.keys()].some(video => video.dataset.customerPlayback === 'manual' && !video.paused && !video.ended);
    };
    const syncVideos = () => {
      videos.forEach((record, video) => {
        if (!record.visible || document.hidden || state.destroyed) {
          video.pause();
          return;
        }
        if (video.dataset.customerPlayback === 'manual') return;
        if (!canPreview(record)) {
          video.pause();
          return;
        }
        if (record.pending || record.blocked || !video.paused) return;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        record.pending = Promise.resolve(playVideo(video)).then(() => {
          record.pending = null;
          if (video.dataset.customerPlayback === 'ambient' && !canPreview(record)) video.pause();
          syncVideoCard(video);
        }).catch(error => {
          record.pending = null;
          record.blocked = error?.name !== 'AbortError';
          syncVideoCard(video);
        });
      });
    };
    const videoVisibility = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const record = videos.get(entry.target);
        if (record) record.visible = entry.isIntersecting && entry.intersectionRatio > 0.05;
      });
      syncVideos();
    }, { threshold: [0, 0.05] });
    const registerVideo = video => {
      if (!video) return;
      video.dataset.customerPlayback = 'ambient';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      const source = cloneSources.get(video.closest('.rc-video-gallery__item'));
      videos.set(video, { visible: false, pending: null, blocked: false, userPaused: false, sourceVideo: source?.querySelector('video') });
      syncVideoCard(video);
      videoVisibility.observe(video);
    };
    originals.forEach(item => registerVideo(item.querySelector('video')));
    const clearCopies = () => {
      copies.flat().forEach(item => {
        const video = item.querySelector('video');
        if (video) {
          const record = videos.get(video);
          if (record) record.visible = false;
          video.pause();
          videoVisibility.unobserve(video);
          videos.delete(video);
        }
        item.remove();
      });
      copies.length = 0;
    };
    const prepareBuffer = () => {
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const cycleWidth = originals.reduce((sum, item) => sum + item.getBoundingClientRect().width + gap, 0);
      const maxSpan = Math.max(0, ...originals.map(item => item.getBoundingClientRect().width + gap));
      const count = enabled && !reducedMotion.matches && originals.length > 1 && cycleWidth > 0 ? Math.max(1, Math.ceil((rail.clientWidth + maxSpan - cycleWidth + gap) / cycleWidth)) : 0;
      if (copies.length === count) return;
      clearCopies();
      // A visual buffer avoids an empty edge even when four cards fill the column.
      // Only the real cards enter the keyboard and accessibility order.
      for (let index = 0; index < count; index++) {
        const set = originals.map(source => {
          const clone = source.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.videoClone = 'true';
          clone.tabIndex = -1;
          [clone, ...clone.querySelectorAll('*')].forEach(element => {
            element.removeAttribute('id');
            [...element.attributes].filter(attribute => attribute.name.startsWith('data-shopify')).forEach(attribute => element.removeAttribute(attribute.name));
          });
          clone.querySelectorAll('button, a, video, [tabindex]').forEach(element => element.tabIndex = -1);
          const video = clone.querySelector('video');
          if (video) {
            video.controls = false;
            video.querySelectorAll('source[data-src]').forEach(source => source.removeAttribute('src'));
          }
          cloneSources.set(clone, source);
          rail.append(clone);
          registerVideo(video);
          return clone;
        });
        copies.push(set);
      }
      state.position = rail.scrollLeft;
      state.lastWritten = rail.scrollLeft;
    };
    const advance = distance => {
      if (Math.abs(rail.scrollLeft - state.lastWritten) > 1) state.position = rail.scrollLeft;
      let position = state.position + distance;
      let guard = originals.length;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      while (guard-- > 0) {
        const first = originals[0];
        const span = first.getBoundingClientRect().width + gap;
        if (span <= 0 || position < span) break;
        rail.insertBefore(first, copies[0]?.[0] || null);
        originals.push(originals.shift());
        copies.forEach((set, index) => {
          rail.insertBefore(set[0], copies[index + 1]?.[0] || null);
          set.push(set.shift());
        });
        position -= span;
      }
      rail.scrollLeft = position;
      state.position = position;
      state.lastWritten = rail.scrollLeft;
    };
    const tick = time => {
      if (state.destroyed) return;
      const elapsed = state.time ? Math.min(time - state.time, 64) : 0;
      state.time = time;
      const canMove = enabled && !reducedMotion.matches && !state.paused && !state.hovered && !state.focused && !state.playing && !state.selected && state.visible && !document.hidden && time >= state.interactionUntil && hasOverflow();
      if (canMove) advance(speed * elapsed / 1000);
      state.frame = requestAnimationFrame(tick);
    };
    const toggleManual = item => {
      const source = cloneSources.get(item) || item;
      const video = source.querySelector('video');
      if (!video) return;
      const wasPlaying = !item.querySelector('video').paused;
      if (source !== item) source.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
      const record = videos.get(video);
      if (record) { record.visible = true; record.blocked = false; record.userPaused = wasPlaying; }
      pauseForInteraction();
      video.dataset.customerPlayback = 'manual';
      video.controls = false;
      video.muted = true;
      state.paused = wasPlaying;
      if (wasPlaying) {
        video.pause();
        syncVideos();
        syncVideoCard(video);
        updatePlaying();
      } else {
        Promise.resolve(playVideo(video)).then(() => {
          if (state.destroyed || document.hidden || record?.userPaused || !record?.visible) video.pause();
          syncVideoCard(video);
          updatePlaying();
        }).catch(() => syncVideoCard(video));
      }
      if (source !== item) source.focus({ preventScroll: true });
    };
    listen(rail, 'pointerenter', event => { if (event.pointerType === 'mouse') state.hovered = true; });
    listen(rail, 'pointerleave', event => { if (event.pointerType === 'mouse') state.hovered = false; });
    listen(rail, 'pointerdown', pauseForInteraction, { passive: true });
    listen(rail, 'pointerup', pauseForInteraction, { passive: true });
    listen(rail, 'wheel', pauseForInteraction, { passive: true });
    listen(rail, 'keydown', event => {
      pauseForInteraction();
      const item = event.target.closest('[data-customer-video-toggle]');
      if (item && ['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        toggleManual(item);
        return;
      }
      if (event.target !== rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const card = rail.firstElementChild;
      const distance = (card?.getBoundingClientRect().width || 118) + (parseFloat(getComputedStyle(rail).columnGap) || 0);
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const maximum = Math.max(0, originals.reduce((sum, item) => sum + item.getBoundingClientRect().width + gap, -gap) - rail.clientWidth);
      rail.scrollTo({ left: Math.max(0, Math.min(maximum, rail.scrollLeft + (event.key === 'ArrowRight' ? distance : -distance))), behavior: 'instant' });
    });
    listen(rail, 'focusin', () => { state.focused = true; });
    listen(rail, 'focusout', () => { queueMicrotask(() => { state.focused = rail.contains(document.activeElement); }); });
    for (const name of ['play', 'pause', 'ended']) listen(rail, name, event => { syncVideoCard(event.target); updatePlaying(); }, { capture: true });
    const preferencesChanged = () => { prepareBuffer(); syncVideos(); };
    listen(reducedMotion, 'change', preferencesChanged);
    if (connection?.addEventListener) listen(connection, 'change', preferencesChanged);
    listen(document, 'visibilitychange', () => { syncVideos(); updatePlaying(); });
    const visibility = new IntersectionObserver(entries => {
      state.visible = entries[0]?.isIntersecting ?? false;
    }, { threshold: 0 });
    visibility.observe(group);
    const resize = new ResizeObserver(prepareBuffer);
    resize.observe(rail);
    const controller = {
      toggle: toggleManual,
      select(item) {
        state.selected = true;
        pauseForInteraction();
        syncVideos();
        item.scrollIntoView({block: 'nearest', inline: 'center', behavior: 'instant'});
      },
      deselect() { state.selected = false; pauseForInteraction(); syncVideos(); },
      destroy() {
        state.destroyed = true;
        cancelAnimationFrame(state.frame);
        events.abort();
        visibility.disconnect();
        videoVisibility.disconnect();
        resize.disconnect();
        videos.forEach((record, video) => video.pause());
        clearCopies();
        videoRails.delete(group);
      }
    };
    videoRails.set(group, controller);
    prepareBuffer();
    state.frame = requestAnimationFrame(tick);
  }
  const galleries = new WeakMap();
  function selectSlide(gallery, index) {
    if (!gallery) return;
    initializeGallery(gallery);
    galleries.get(gallery)?.select(index);
  }
  function initializeGallery(gallery) {
    if (galleries.has(gallery)) return;
    const surface = gallery.querySelector('[data-gallery-swipe]');
    if (!surface) return;
    const slides = [...surface.querySelectorAll('[data-slide]')];
    if (!slides.length) return;
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const strip = gallery.querySelector('.rc-gallery__thumbs');
    const events = new AbortController();
    const signal = events.signal;
    let activeIndex = -1;
    let requestedIndex = null;
    let positions = [];
    let measuredWidth = 0;
    let scrollFrame = 0;
    let resizeFrame = 0;
    let settleTimer = 0;
    const normalize = index => ((Math.trunc(index) % slides.length) + slides.length) % slides.length;
    const measure = () => {
      const start = slides[0].offsetLeft;
      positions = slides.map(slide => slide.offsetLeft - start);
      measuredWidth = surface.clientWidth;
    };
    const nearestIndex = () => {
      if (!measuredWidth || Math.abs(surface.clientWidth - measuredWidth) > 0.5) return Math.max(0, activeIndex);
      let nearest = 0;
      positions.forEach((position, index) => {
        if (Math.abs(surface.scrollLeft - position) < Math.abs(surface.scrollLeft - positions[nearest])) nearest = index;
      });
      return nearest;
    };
    const pauseMedia = slide => {
      slide.querySelectorAll('video').forEach(video => video.pause());
      slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      slide.querySelectorAll('iframe').forEach(frame => {
        if (!frame.src || !frame.contentWindow) return;
        let source;
        try { source = new URL(frame.src, location.href); } catch { return; }
        if (/^(www\.)?youtube(-nocookie)?\.com$/.test(source.hostname)) {
          frame.contentWindow.postMessage(JSON.stringify({event: 'command', func: 'pauseVideo', args: []}), source.origin);
        } else if (source.hostname === 'player.vimeo.com') {
          frame.contentWindow.postMessage({method: 'pause'}, source.origin);
        }
      });
    };
    const updateActive = index => {
      if (index === activeIndex) return;
      activeIndex = index;
      gallery.dataset.activeSlide = String(index);
      slides.forEach((slide, i) => {
        const inactive = i !== index;
        slide.inert = inactive;
        slide.setAttribute('aria-hidden', String(inactive));
        if (inactive) pauseMedia(slide);
      });
      thumbs.forEach(thumb => thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.thumb) === index)));
    };
    const revealThumb = () => {
      const thumb = thumbs.find(item => Number(item.dataset.thumb) === activeIndex);
      if (!strip || !thumb) return;
      const viewport = strip.getBoundingClientRect();
      const bounds = thumb.getBoundingClientRect();
      const delta = bounds.left < viewport.left ? bounds.left - viewport.left : bounds.right > viewport.right ? bounds.right - viewport.right : 0;
      if (Math.abs(delta) > 1) strip.scrollTo({left: strip.scrollLeft + delta, behavior: 'instant'});
    };
    const settle = () => {
      clearTimeout(settleTimer);
      if (Math.abs(surface.clientWidth - measuredWidth) > 0.5) return;
      requestedIndex = null;
      updateActive(nearestIndex());
      revealThumb();
    };
    const select = index => {
      if (!Number.isFinite(index)) return activeIndex;
      const chosen = normalize(index);
      requestedIndex = chosen;
      if (!measuredWidth) {
        updateActive(chosen);
        return chosen;
      }
      const behavior = videoMotion.matches ? 'instant' : 'smooth';
      surface.scrollTo({left: positions[chosen], behavior});
      if (behavior === 'instant') updateActive(chosen);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 180);
      return chosen;
    };
    surface.addEventListener('scroll', () => {
      if (!scrollFrame) {
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = 0;
          updateActive(nearestIndex());
        });
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 180);
    }, {signal, passive: true});
    surface.addEventListener('scrollend', settle, {signal, passive: true});
    gallery.addEventListener('keydown', event => {
      const thumb = event.target.closest('[data-thumb]');
      if (!thumb && event.target !== surface) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const current = requestedIndex ?? activeIndex;
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
      const chosen = select(next);
      if (thumb) thumbs.find(item => Number(item.dataset.thumb) === chosen)?.focus({preventScroll: true});
    }, { signal });
    const resize = new ResizeObserver(() => {
      if (Math.abs(surface.clientWidth - measuredWidth) < 0.5) return;
      cancelAnimationFrame(scrollFrame);
      scrollFrame = 0;
      cancelAnimationFrame(resizeFrame);
      clearTimeout(settleTimer);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        const chosen = requestedIndex ?? Math.max(0, activeIndex);
        measure();
        surface.scrollTo({left: positions[chosen], behavior: 'instant'});
        requestedIndex = null;
        updateActive(chosen);
        revealThumb();
      });
    });
    const initial = Number(gallery.dataset.activeSlide || gallery.querySelector('[data-thumb][aria-pressed="true"]')?.dataset.thumb || 0);
    measure();
    updateActive(normalize(Number.isFinite(initial) ? initial : 0));
    surface.scrollTo({left: positions[activeIndex], behavior: 'instant'});
    resize.observe(surface);
    galleries.set(gallery, {
      select,
      destroy() {
        cancelAnimationFrame(scrollFrame);
        cancelAnimationFrame(resizeFrame);
        clearTimeout(settleTimer);
        events.abort();
        resize.disconnect();
        galleries.delete(gallery);
      }
    });
  }
  function selectQuote(container, index) {
    if (!container) return;
    const quotes = [...container.querySelectorAll('.rc-quote')];
    if (!quotes.length) return;
    const chosen = Math.max(0, Math.min(index, quotes.length - 1));
    quotes.forEach((quote, i) => quote.hidden = i !== chosen);
    container.querySelectorAll('[data-quote]').forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === chosen)));
    container.dataset.ready = 'true';
  }
  function initialize(root = document) {
    initializeDeferredVideos(root);
    root.querySelectorAll('[data-quotes]').forEach(container => selectQuote(container, 0));
    root.querySelectorAll('[data-video-marquee]').forEach(initializeVideoRail);
    root.querySelectorAll('[data-gallery]').forEach(initializeGallery);
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    destroyDeferredVideos(event.target);
    event.target.querySelectorAll('[data-gallery]').forEach(gallery => galleries.get(gallery)?.destroy());
    event.target.querySelectorAll('[data-video-marquee]').forEach(group => videoRails.get(group)?.destroy());
    event.target.querySelectorAll('video').forEach(video => video.pause());
  });
  document.addEventListener('click', event => {
    const videoArrow = event.target.closest('[data-video-step]');
    if (videoArrow) {
      const rail = videoArrow.closest('.rc-video-gallery')?.querySelector('.rc-video-gallery__track');
      rail?.scrollBy({left: Number(videoArrow.dataset.videoStep) * 320, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
    const videoToggle = event.target.closest('[data-customer-video-toggle]');
    if (videoToggle) {
      const item = videoToggle.closest('.rc-video-gallery__item');
      const group = item?.closest('[data-video-marquee]');
      const controller = group && videoRails.get(group);
      if (controller) controller.toggle(item);
      else {
        const video = item?.querySelector('video');
        if (video) {
          video.dataset.customerPlayback = 'manual';
          video.controls = false;
          video.muted = true;
          if (video.paused) playVideo(video).catch(() => {});
          else video.pause();
        }
      }
    }
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) selectSlide(thumb.closest('[data-gallery]'), Number(thumb.dataset.thumb));
    const quote = event.target.closest('[data-quote]');
    if (quote) selectQuote(quote.closest('[data-quotes]'), Number(quote.dataset.quote));
  });
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const form = select.closest('form');
    const option = select.selectedOptions[0];
    if (!form || !option) return;
    const button = form.querySelector('[name="add"]');
    const available = option.dataset.available === 'true';
    if (button) {
      button.disabled = !available;
      button.textContent = available ? button.dataset.label : button.dataset.soldOut;
    }
    const price = form.querySelector('[data-price]');
    if (price) price.textContent = option.dataset.price;
    const comparePrice = form.querySelector('[data-compare-price]');
    if (comparePrice) {
      comparePrice.textContent = option.dataset.compare || '';
      comparePrice.hidden = !option.dataset.compare;
    }
    const quantity = form.querySelector('[name="quantity"]');
    if (quantity) {
      const minimum = Number(option.dataset.quantityMin) || 1;
      const increment = Number(option.dataset.quantityIncrement) || 1;
      const maximum = Number(option.dataset.quantityMax) || Infinity;
      quantity.min = String(minimum);
      quantity.step = String(increment);
      if (Number.isFinite(maximum)) quantity.max = String(maximum);
      else quantity.removeAttribute('max');
      const desired = Math.min(maximum, Math.max(minimum, Number(quantity.value) || minimum));
      quantity.value = String(minimum + Math.floor((desired - minimum) / increment) * increment);
    }
    const section = select.closest('[data-product-section]');
    const stock = section?.querySelector('[data-stock-status][data-stock-mode="inventory"]');
    if (stock) {
      const count = Math.max(0, Number(option.dataset.inventory) || 0);
      const tracked = Boolean(option.dataset.inventoryManaged) && option.dataset.inventoryPolicy !== 'continue';
      const stockCount = stock.querySelector('[data-stock-count]');
      const stockFill = stock.querySelector('[data-stock-fill]');
      stock.classList.toggle('is-low', tracked && count <= Number(stock.dataset.stockThreshold));
      stock.classList.toggle('is-sold-out', !available);
      if (stockCount) stockCount.textContent = !available ? stock.dataset.soldOut : tracked && stock.dataset.showCount === 'true' ? stock.dataset.stockTemplate.replace('[count]', String(count)) : stock.dataset.inStock;
      if (stockFill) stockFill.style.width = `${!available ? 0 : tracked ? Math.min(100, count * 100 / (Number(stock.dataset.stockScale) || 10)) : 100}%`;
    }
    if (option.dataset.mediaId) {
      const gallery = section?.querySelector('[data-gallery]');
      const index = [...(gallery?.querySelectorAll('[data-slide]') || [])].findIndex(slide => slide.dataset.mediaId === option.dataset.mediaId);
      if (index >= 0) selectSlide(gallery, index);
    }
    const url = new URL(location.href);
    url.searchParams.set('variant', option.value);
    history.replaceState(null, '', url);
  });
  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-slide]');
    if (slide) selectSlide(slide.closest('[data-gallery]'), Number(slide.dataset.slide));
    const quote = event.target.closest('.rc-quote');
    if (quote) selectQuote(quote.closest('[data-quotes]'), [...quote.parentElement.querySelectorAll('.rc-quote')].indexOf(quote));
    const videoItem = event.target.closest('.rc-video-gallery__item');
    if (videoItem) {
      const group = videoItem.closest('[data-video-marquee]');
      if (group) videoRails.get(group)?.select(videoItem);
      else videoItem.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }
  });
  document.addEventListener('shopify:block:deselect', event => {
    const group = event.target.closest('[data-video-marquee]');
    if (group) videoRails.get(group)?.deselect();
  });
  document.addEventListener('toggle', event => {
    if (event.target.matches?.('.rc-video-gallery') && !event.target.open) event.target.querySelectorAll('video').forEach(video => video.pause());
  }, true);
  document.addEventListener('play', event => {
    if (!(event.target instanceof HTMLVideoElement) || event.target.dataset.customerPlayback === 'ambient') return;
    document.querySelectorAll('video').forEach(video => {
      if (video !== event.target && (video.controls || video.dataset.customerPlayback === 'manual') && !video.paused) video.pause();
    });
  }, true);
})();
